import React, { useState, useEffect } from "react";
import { IconButton, Message, useToaster, Input, InputGroup } from "rsuite";
import SettingIcon from "@rsuite/icons/Setting";
import PlusIcon from "@rsuite/icons/Plus";
import CloseIcon from "@rsuite/icons/Close";
import SearchIcon from "@rsuite/icons/Search";
import LocationIcon from "@rsuite/icons/Location";
import PcIcon from "@rsuite/icons/Pc";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import Modal from "../../../../../ui/Modal/Modal";
import Button from "../../../../../ui/Button/Button";
import { Card } from "../../../../../ui/";
import styled from "styled-components";
import { colors } from "../../../../../ui/theme/colors";

export const ManageSitesInGroup = ({
  group,
  sites,
  siteGroupMappings,
  customer,
  update,
  setUpdate,
  onMappingsUpdate, // Add this prop from parent to update mappings directly
  refreshSiteGroups, // Add this prop to refresh site groups without full update
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const toaster = useToaster();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Local state for optimistic updates
  const [localMappings, setLocalMappings] = useState(siteGroupMappings);
  // Track if any changes were made
  const [hasChanges, setHasChanges] = useState(false);

  // Sync local state when props change
  useEffect(() => {
    setLocalMappings(siteGroupMappings);
  }, [siteGroupMappings]);

  const handleOpen = () => {
    setHasChanges(false);
    setTimeout(() => setOpen(true), 100);
  };

  const handleClose = async () => {
    setOpen(false);
    setSearchTerm("");

    // If changes were made and we need to refresh the parent data
    if (hasChanges) {
      // If parent provided an update callback, use it
      if (onMappingsUpdate) {
        onMappingsUpdate(localMappings);
      } else {
        // Fallback to triggering full refresh
        setUpdate(!update);
      }

      // Refresh site groups to get updated timestamps without affecting modal state
      if (refreshSiteGroups) {
        await refreshSiteGroups();
      }
    }

    setTimeout(() => {
      setLoading(false);
    }, 50);
  };

  const successMessage = (message) => (
    <Message showIcon type="success" header="Success">
      {message}
    </Message>
  );

  const errorMessage = (
    <Message showIcon type="error" header="Error">
      Failed to update site assignment. Please try again.
    </Message>
  );

  const getSitesInGroup = () => {
    const siteIdsInGroup = localMappings
      .filter((mapping) => mapping.site_group_id === group.id)
      .map((mapping) => mapping.site_id);
    return sites.filter((site) => siteIdsInGroup.includes(site.id));
  };

  const getAvailableSites = () => {
    // Get all sites that are in any group
    const allAssignedSiteIds = localMappings.map((mapping) => mapping.site_id);
    // Return only sites that are not in any group
    return sites.filter((site) => !allAssignedSiteIds.includes(site.id));
  };

  const filterSites = (sitesList) => {
    if (!searchTerm) return sitesList;
    return sitesList.filter(
      (site) =>
        site.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.state?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const addSiteToGroup = async (siteId) => {
    setLoading(true);

    // Store original state for rollback if needed
    const originalMappings = [...localMappings];

    // Optimistically update local state
    const newMapping = {
      site_group_id: group.id,
      site_id: siteId,
      customer_id: customer, // Include customer_id if needed
    };
    const updatedMappings = [...localMappings, newMapping];
    setLocalMappings(updatedMappings);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/customer/site-group-mappings`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          action: "assign",
          site_group_id: group.id,
          site_ids: [siteId],
          customer_id: customer,
          created_by: user?.email,
          last_updated_by: user?.email,
        }),
      });

      // Mark that changes were made
      setHasChanges(true);

      // Update parent state immediately if callback is provided
      if (onMappingsUpdate) {
        onMappingsUpdate(updatedMappings);
      }

      toaster.push(
        successMessage("Site added to group successfully."),
        "topCenter",
      );
    } catch (error) {
      console.error("Error adding site to group:", error);
      // Revert optimistic update on error
      setLocalMappings(originalMappings);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoading(false);
    }
  };

  const removeSiteFromGroup = async (siteId) => {
    setLoading(true);

    // Store original state for rollback if needed
    const originalMappings = [...localMappings];

    // Optimistically update local state
    const updatedMappings = localMappings.filter(
      (mapping) =>
        !(mapping.site_group_id === group.id && mapping.site_id === siteId),
    );
    setLocalMappings(updatedMappings);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/customer/site-group-mappings`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          action: "remove",
          site_group_id: group.id,
          site_ids: [siteId],
          customer_id: customer,
          last_updated_by: user?.email,
        }),
      });

      // Mark that changes were made
      setHasChanges(true);

      // Update parent state immediately if callback is provided
      if (onMappingsUpdate) {
        onMappingsUpdate(updatedMappings);
      }

      toaster.push(
        successMessage("Site removed from group successfully."),
        "topCenter",
      );
    } catch (error) {
      console.error("Error removing site from group:", error);
      // Revert optimistic update on error
      setLocalMappings(originalMappings);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoading(false);
    }
  };

  const getOtherGroupsForSite = (siteId) => {
    const groupIdsForSite = localMappings
      .filter(
        (mapping) =>
          mapping.site_id === siteId && mapping.site_group_id !== group.id,
      )
      .map((mapping) => mapping.site_group_id);
    return groupIdsForSite;
  };

  const sitesInGroup = getSitesInGroup();
  const availableSites = getAvailableSites();
  const filteredSitesInGroup = filterSites(sitesInGroup);
  const filteredAvailableSites = filterSites(availableSites);

  return (
    <>
      <IconButton
        icon={<SettingIcon />}
        onClick={handleOpen}
        size="sm"
        appearance="subtle"
      />

      <Modal
        open={open}
        onClose={handleClose}
        title={`Manage Sites in Group: ${group.name}`}
        size="xxl"
        showFooter={true}
        footerActions={
          <StyledFooterActions>
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </StyledFooterActions>
        }
      >
        <ModalContent>
          <SearchContainer>
            <InputGroup>
              <Input
                placeholder="Search sites..."
                value={searchTerm}
                onChange={setSearchTerm}
              />
              <InputGroup.Addon>
                <SearchIcon />
              </InputGroup.Addon>
            </InputGroup>
          </SearchContainer>

          <SitesContainer>
            <SiteColumn>
              <ColumnHeader>
                <ColumnTitle>
                  Sites in Group ({filteredSitesInGroup.length})
                </ColumnTitle>
                <ColumnSubtitle>
                  Click the × to remove sites from this group
                </ColumnSubtitle>
              </ColumnHeader>
              <SiteList>
                {filteredSitesInGroup.length > 0 ? (
                  <SiteGrid>
                    {filteredSitesInGroup.map((site) => (
                      <SiteCardWrapper key={site.id}>
                        <Card variant="success">
                          <SiteCardContent>
                            <SiteMainInfo>
                              <SiteIcon>
                                <LocationIcon />
                              </SiteIcon>
                              <SiteInfo>
                                <SiteName>{site.name}</SiteName>
                                <SiteDetails>
                                  <DetailItem>
                                    <LocationIcon
                                      style={{
                                        fontSize: "12px",
                                        marginRight: "4px",
                                      }}
                                    />
                                    {site.city}, {site.state}
                                  </DetailItem>
                                  <DetailItem>
                                    <PcIcon
                                      style={{
                                        fontSize: "12px",
                                        marginRight: "4px",
                                      }}
                                    />
                                    {site.systems} systems
                                  </DetailItem>
                                </SiteDetails>
                              </SiteInfo>
                            </SiteMainInfo>
                          </SiteCardContent>
                        </Card>
                        <SiteActions>
                          <RemoveButton
                            onClick={() => removeSiteFromGroup(site.id)}
                            disabled={loading}
                            title="Remove from group"
                          >
                            <CloseIcon />
                          </RemoveButton>
                        </SiteActions>
                      </SiteCardWrapper>
                    ))}
                  </SiteGrid>
                ) : (
                  <EmptyState>
                    {searchTerm
                      ? "No sites found matching your search."
                      : "No sites in this group yet."}
                  </EmptyState>
                )}
              </SiteList>
            </SiteColumn>

            <SiteColumn>
              <ColumnHeader>
                <ColumnTitle>
                  Available Sites ({filteredAvailableSites.length})
                </ColumnTitle>
                <ColumnSubtitle>
                  Click the + to add unassigned sites to this group
                </ColumnSubtitle>
              </ColumnHeader>
              <SiteList>
                {filteredAvailableSites.length > 0 ? (
                  <SiteGrid>
                    {filteredAvailableSites.map((site) => (
                      <SiteCardWrapper key={site.id}>
                        <Card variant="default">
                          <SiteCardContent>
                            <SiteMainInfo>
                              <SiteIcon>
                                <LocationIcon />
                              </SiteIcon>
                              <SiteInfo>
                                <SiteName>{site.name}</SiteName>
                                <SiteDetails>
                                  <DetailItem>
                                    <LocationIcon
                                      style={{
                                        fontSize: "12px",
                                        marginRight: "4px",
                                      }}
                                    />
                                    {site.city}, {site.state}
                                  </DetailItem>
                                  <DetailItem>
                                    <PcIcon
                                      style={{
                                        fontSize: "12px",
                                        marginRight: "4px",
                                      }}
                                    />
                                    {site.systems} systems
                                  </DetailItem>
                                  {getOtherGroupsForSite(site.id).length >
                                    0 && (
                                    <GroupTag>
                                      In {getOtherGroupsForSite(site.id).length}{" "}
                                      other group
                                      {getOtherGroupsForSite(site.id).length > 1
                                        ? "s"
                                        : ""}
                                    </GroupTag>
                                  )}
                                </SiteDetails>
                              </SiteInfo>
                            </SiteMainInfo>
                          </SiteCardContent>
                        </Card>
                        <SiteActions>
                          <AddButton
                            onClick={() => addSiteToGroup(site.id)}
                            disabled={loading}
                            title="Add to group"
                          >
                            <PlusIcon />
                          </AddButton>
                        </SiteActions>
                      </SiteCardWrapper>
                    ))}
                  </SiteGrid>
                ) : (
                  <EmptyState>
                    {searchTerm
                      ? "No available sites found matching your search."
                      : "No available unassigned sites found."}
                  </EmptyState>
                )}
              </SiteList>
            </SiteColumn>
          </SitesContainer>
        </ModalContent>
      </Modal>
    </>
  );
};

const ModalContent = styled.div`
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent this container from scrolling */
`;

const StyledFooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;

  .rs-input-group {
    border-radius: 8px;
    overflow: hidden;
  }

  .rs-input {
    border: 1px solid ${colors.neutral[300]};
    border-radius: 8px 0 0 8px;
    padding: 12px 16px;
    font-size: 14px;

    &:focus {
      border-color: ${colors.primary.main};
      box-shadow: 0 0 0 3px ${colors.primary.main}20;
    }
  }

  .rs-input-group-addon {
    background: ${colors.neutral[100]};
    border: 1px solid ${colors.neutral[300]};
    border-left: none;
    color: ${colors.neutral[500]};
  }
`;

const SitesContainer = styled.div`
  display: flex;
  gap: 24px;
  flex: 1; /* Take up remaining space after search */
  min-height: 0; /* Allow flex shrinking */
`;

const SiteColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Allow flex children to shrink */
`;

const ColumnHeader = styled.div`
  flex-shrink: 0; /* Prevent header from shrinking */
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${colors.primary.main};
  background: white; /* Ensure header stays visible */
  z-index: 2;
  position: relative; /* Ensure proper stacking context */
`;

const ColumnTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const ColumnSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${colors.neutral[500]};
  line-height: 1.4;
`;

const SiteList = styled.div`
  flex: 1;
  overflow-y: auto; /* Enable vertical scrolling for each list */
  overflow-x: hidden; /* Prevent horizontal scrolling */
  padding-right: 8px;
  min-height: 0; /* Critical: Allow flex item to shrink below content size */
  max-height: 100%; /* Ensure it doesn't exceed container */

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.neutral[100]};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral[300]};
    border-radius: 3px;

    &:hover {
      background: ${colors.neutral[400]};
    }
  }
`;

const SiteGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px; /* Tighter gaps between cards */
`;

const SiteCardWrapper = styled.div`
  position: relative; /* Provide positioning context for absolute child elements */
  width: 100%;
`;

const SiteCardContent = styled.div`
  padding: 6px 10px !important; /* Override Card's default padding */
  display: flex;
  align-items: center;
  min-height: 36px; /* More compact */
  margin: 0 !important; /* Remove any inherited margins */
`;

const SiteMainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0; /* Allow content to shrink */
`;

const SiteIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${colors.primary.main}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary.main};
  font-size: 14px;
  flex-shrink: 0;
`;

const SiteInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SiteName = styled.h4`
  margin: 0 0 2px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  line-height: 1.2;
`;

const SiteDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${colors.neutral[600]};
  line-height: 1.3;
`;

const GroupTag = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${colors.warning.main};
  font-weight: 500;
  margin-top: 2px;
`;

const SiteActions = styled.div`
  position: absolute;
  right: 12px; /* Position on far right edge of the entire card wrapper */
  top: 50%;
  transform: translateY(-50%); /* Center vertically within the wrapper */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1; /* Ensure buttons appear above card content */
`;

const AddButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: ${colors.success.light};
  color: ${colors.success.main};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: ${colors.success.main};
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: ${colors.error.light};
  color: ${colors.error.main};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: ${colors.error.main};
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: ${colors.neutral[50]};
  border: 2px dashed ${colors.neutral[300]};
  border-radius: 12px;
  color: ${colors.neutral[500]};
  font-style: italic;
  min-height: 200px;
`;
